// ==================== FORM VALIDATION SCHEMAS ====================

import { z } from 'zod';

// ==================== COMMON VALIDATION RULES ====================

export const validationRules = {
  // String validations
  required: (fieldName: string) => z.string().min(1, `${fieldName} is required`),
  
  email: z.string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[@$!%*?&]/, 'Password must contain at least one special character (@$!%*?&)'),
  
  phone: z.string()
    .min(1, 'Phone number is required')
    .regex(/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/, 'Please enter a valid phone number'),
  
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be less than 50 characters'),
  
  url: z.string()
    .url('Please enter a valid URL')
    .optional()
    .or(z.literal('')),
  
  // Number validations
  positiveNumber: z.number().positive('Must be a positive number'),
  
  // Optional versions
  optionalEmail: z.string().email('Please enter a valid email').optional().or(z.literal('')),
  optionalPhone: z.string().regex(/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/, 'Please enter a valid phone number').optional().or(z.literal('')),
};

// ==================== CONTACT FORM SCHEMA ====================

export const contactFormSchema = z.object({
  firstName: validationRules.name,
  lastName: validationRules.name,
  email: validationRules.email,
  phone: validationRules.optionalPhone,
  company: z.string().optional(),
  subject: z.string().min(1, 'Please select a subject'),
  message: z.string()
    .min(10, 'Message must be at least 10 characters')
    .max(1000, 'Message must be less than 1000 characters'),
  agreeToTerms: z.boolean().refine(val => val === true, {
    message: 'You must agree to the terms and conditions',
  }),
  website: z.string().optional(),
});

export type ContactFormData = z.infer<typeof contactFormSchema>;

// ==================== LOGIN FORM SCHEMA ====================

export const loginFormSchema = z.object({
  email: validationRules.email,
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional(),
});

export type LoginFormData = z.infer<typeof loginFormSchema>;

// ==================== REGISTER FORM SCHEMA ====================

export const registerFormSchema = z.object({
  firstName: validationRules.name,
  lastName: validationRules.name,
  email: validationRules.email,
  password: validationRules.password,
  confirmPassword: z.string().min(1, 'Please confirm your password'),
  companyName: z.string().min(2, 'Company name must be at least 2 characters'),
  phone: validationRules.optionalPhone,
  agreeToTerms: z.boolean().refine(val => val === true, {
    message: 'You must agree to the terms and conditions',
  }),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export type RegisterFormData = z.infer<typeof registerFormSchema>;

// ==================== NEWSLETTER FORM SCHEMA ====================

export const newsletterFormSchema = z.object({
  email: validationRules.email,
});

export type NewsletterFormData = z.infer<typeof newsletterFormSchema>;

// ==================== PROFILE UPDATE SCHEMA ====================

export const profileUpdateSchema = z.object({
  firstName: validationRules.name,
  lastName: validationRules.name,
  email: validationRules.email,
  phone: validationRules.optionalPhone,
  company: z.string().optional(),
  bio: z.string().max(500, 'Bio must be less than 500 characters').optional(),
  website: validationRules.url,
  notifications: z.object({
    email: z.boolean(),
    sms: z.boolean(),
    push: z.boolean(),
  }).optional(),
});

export type ProfileUpdateData = z.infer<typeof profileUpdateSchema>;


// ==================== SUB-ACCOUNT FORM SCHEMAS ====================

// The team member's contact details (the phone number follows the contact form's rules)
const subAccountContactDetails = {
  personalEmail: z.string()
    .min(1, 'Personal email is required')
    .email('Please enter a valid email address'),
  phoneNumber: z.string()
    .min(10, 'Phone number must be at least 10 digits')
    .max(20, 'Phone number is too long')
    .regex(/^[\d+\-\s()]+$/, 'Invalid phone number format'),
};

export const subAccountCreateSchema = z.object({
  firstName: validationRules.name,
  lastName: validationRules.name,
  email: validationRules.email,
  ...subAccountContactDetails,
  password: validationRules.password,
  confirmPassword: z.string().min(1, 'Please confirm the password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export type SubAccountCreateData = z.infer<typeof subAccountCreateSchema>;

export const subAccountEditSchema = z.object({
  firstName: validationRules.name,
  lastName: validationRules.name,
  ...subAccountContactDetails,
});

export type SubAccountEditData = z.infer<typeof subAccountEditSchema>;

export const subAccountPasswordSchema = z.object({
  password: validationRules.password,
  confirmPassword: z.string().min(1, 'Please confirm the password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export type SubAccountPasswordData = z.infer<typeof subAccountPasswordSchema>;
